import React, { useContext } from "react";
import { styled, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import MoreIcon from "@mui/icons-material/MoreVert";
import LogoutIcon from "@mui/icons-material/Logout";
import Avatar from "@mui/material/Avatar";
import { useTranslation } from "react-i18next";
import ThemeContext from "../../ThemeContext";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserDecoded } from "../../redux_files/authSlice";
import api from "../../utils/axiosAuth";
import globalState from "../../utils/globalState";
import AppLogo from "./AppLogo";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const Appheader = ({ onMenuClick, handleLogout }) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };
  const { mode, setMode } = useContext(ThemeContext);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const userDecoded = useSelector(selectUserDecoded);
  const [userNames, setUserNames] = React.useState({ ar: "", en: "" });
  const [avatarUrl, setAvatarUrl] = React.useState(null);

  const applyUserData = React.useCallback((userData) => {
    if (!userData) return;
    setUserNames({
      ar: userData.full_name_ar || userData.username || "",
      en: userData.full_name_en || userData.username || "",
    });
    setAvatarUrl(userData.avatar || null);
  }, []);

  React.useEffect(() => {
    if (userDecoded?.user_id) {
      api.get(`/api/users/${userDecoded.user_id}/`).then((res) => {
        const userData = res.data;
        applyUserData(userData);
        globalState.setCurrentUser(userData);
      });
    }
  }, [userDecoded?.user_id, applyUserData]);

  React.useEffect(() => {
    return globalState.addListener((user) => {
      if (!user) return;
      setUserNames({
        ar: user.fullNameAr || user.username || "",
        en: user.fullNameEn || user.username || "",
      });
      setAvatarUrl(user.avatar || null);
    });
  }, []);
  let displayName = userDecoded?.username || "";
  if (i18n.language === "ar" && userNames.ar) {
    displayName = userNames.ar;
  } else if (i18n.language === "en" && userNames.en) {
    displayName = userNames.en;
  }

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => {
          handleMenuClose();
          navigate("/profile");
        }}
      >
        <Tooltip title={t("HeaderProfileTooltip")}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            {displayName}
          </Typography>
        </Tooltip>
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleMenuClose();
          handleLogout();
          navigate("/");
        }}
      >
        {t("logout")}
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem
        onClick={() => {
          handleMenuClose();
          navigate("/profile");
        }}
      >
        <Tooltip title={t("HeaderProfileTooltip")}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            {displayName}
          </Typography>
        </Tooltip>
      </MenuItem>
      <MenuItem onClick={toggleLanguage}>
        <Tooltip title={t("HeaderLanguageTooltip")}>
        <IconButton size="large" aria-label={t("HeaderLanguageTooltip")} color="inherit">
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: "primary.main",
              fontSize: 16,
              fontWeight: "bold",
            }}
          >
            {currentLang === "ar" ? "E" : "ع"}
          </Avatar>
        </IconButton>
        </Tooltip>
        <p> {currentLang === "ar" ? t("English") : t("Arabic")}</p>
      </MenuItem>
      <MenuItem
        onClick={() => {
          const newMode = mode === "light" ? "dark" : "light";
          setMode(newMode);
          localStorage.setItem("themeMode", newMode);
        }}
      >
        <Tooltip title={t("HeaderThemeTooltip")}>
        <IconButton
          size="large"
          aria-label={t("HeaderThemeTooltip")}
          color="inherit"
        >
          {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
        </IconButton>
        </Tooltip>
        <p>{mode === "light" ? t("DarkMode") : t("LightMode")}</p>
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleMenuClose();
          handleLogout();
        }}
      >
        <Tooltip title={t("HeaderLogoutTooltip")}>
        <IconButton
          size="large"
          aria-label={t("HeaderLogoutTooltip")}
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <LogoutIcon />
        </IconButton>
        </Tooltip>
        <p>{t("logout")}</p>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <Tooltip title={t("HeaderMenuTooltip")}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label={t("HeaderMenuTooltip")}
              sx={{ mr: 2 }}
              onClick={onMenuClick}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <AppLogo height={40} sx={{ mr: 1, display: { xs: "none", sm: "block" } }} />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: "bold", display: { xs: "none", sm: "block" } }}
          >
            {t("reportTitle")}
          </Typography>
          {/* <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ "aria-label": "search" }}
            />
          </Search> */}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: "none", md: "flex", lg: "none" } }}>
            <Tooltip title={t("HeaderLanguageTooltip")}>
              <IconButton
                size="large"
                aria-label={t("HeaderLanguageTooltip")}
                color="inherit"
                onClick={toggleLanguage}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "primary.main",
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                >
                  {currentLang === "ar" ? "E" : "ع"}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Tooltip title={t("HeaderThemeTooltip")}>
              <IconButton
                size="large"
                aria-label={t("HeaderThemeTooltip")}
                color="inherit"
                onClick={() => {
                  const newMode = mode === "light" ? "dark" : "light";
                  setMode(newMode);
                  localStorage.setItem("themeMode", newMode);
                }}
              >
                {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("HeaderProfileTooltip")}>
              <IconButton
                size="large"
                edge="end"
                aria-label={t("HeaderProfileTooltip")}
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar
                  alt={displayName || "user"}
                  src={avatarUrl || "/static/images/user2.png"}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center" }}>
            <Tooltip title={t("HeaderLanguageTooltip")}>
              <IconButton
                size="large"
                aria-label={t("HeaderLanguageTooltip")}
                color="inherit"
                onClick={toggleLanguage}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "primary.main",
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                >
                  {currentLang === "ar" ? "E" : "ع"}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Tooltip title={t("HeaderThemeTooltip")}>
              <IconButton
                size="large"
                aria-label={t("HeaderThemeTooltip")}
                color="inherit"
                onClick={() => {
                  const newMode = mode === "light" ? "dark" : "light";
                  setMode(newMode);
                  localStorage.setItem("themeMode", newMode);
                }}
              >
                {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("HeaderProfileTooltip")}>
              <Button
                color="inherit"
                aria-label={t("HeaderProfileTooltip")}
                onClick={() => navigate("/profile")}
                startIcon={
                  <Avatar
                    alt={displayName || "user"}
                    src={avatarUrl || "/static/images/user2.png"}
                    sx={{ width: 36, height: 36 }}
                  />
                }
                sx={{ textTransform: "none", fontWeight: 600, mx: 0.5 }}
              >
                {displayName}
              </Button>
            </Tooltip>
            <Tooltip title={t("HeaderLogoutTooltip")}>
              <IconButton
                size="large"
                edge="end"
                aria-label={t("HeaderLogoutTooltip")}
                color="inherit"
                onClick={() => {
                  handleLogout();
                  navigate("/");
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <Tooltip title={t("HeaderMoreTooltip")}>
              <IconButton
                size="large"
                aria-label={t("HeaderMoreTooltip")}
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit"
              >
                <MoreIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
};

export default Appheader;
